import type { PartialPaymentPolicyResponse } from '@/types/partial-payment';
import { computePartialPaymentBounds } from '@/types/partial-payment';

function unwrapPolicyPayload(json: unknown): PartialPaymentPolicyResponse | null {
  if (!json || typeof json !== 'object') {
    return null;
  }
  const root = json as Record<string, unknown>;
  let payload = root.data ?? root;
  if (payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>)) {
    const inner = (payload as Record<string, unknown>).data;
    if (inner && typeof inner === 'object' && 'policy' in (inner as Record<string, unknown>)) {
      payload = inner;
    }
  }
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const p = payload as PartialPaymentPolicyResponse;
  if (!p.policy) {
    return null;
  }
  return p;
}

export async function fetchPartialPaymentPolicy(
  productId: number,
  totalAmount?: number,
): Promise<PartialPaymentPolicyResponse | null> {
  const safeTotal =
    totalAmount != null && Number.isFinite(totalAmount) && totalAmount > 0
      ? Math.round(totalAmount * 100) / 100
      : null;
  const url =
    safeTotal != null
      ? `/api/products/by-id/${productId}/partial-payment-policy?total_amount=${encodeURIComponent(String(safeTotal))}`
      : `/api/products/by-id/${productId}/partial-payment-policy`;
  const res = await fetch(url, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    return null;
  }
  const json = await res.json();
  const payload = unwrapPolicyPayload(json);
  if (!payload || !payload.policy.enabled) {
    return null;
  }

  if (totalAmount != null && totalAmount > 0 && !payload.bounds) {
    payload.bounds = computePartialPaymentBounds(payload.policy, totalAmount);
  }

  return payload;
}
